import type {
  Observer,
  ObservableLike,
  Unsubscribable,
  PascalCase,
  Simplify,
} from 'type-fest';

import {
  callCompleteObservers,
  callErrorObservers,
  callNextObservers,
} from '@frontend/utils';

type SubscribeMethodName<Property extends string> =
  `subscribe${PascalCase<Property>}`;

type Subscribes<Properties extends Record<string, unknown>> = {
  [Property in Extract<
    keyof Properties,
    string
  > as SubscribeMethodName<Property>]: (
    observer: Partial<Observer<Properties[Property]>>,
  ) => Unsubscribable;
};

type Store<Properties extends Record<string, unknown>> = Simplify<
  Properties & Subscribes<Properties>
>;

type StoreValidators<Properties extends Record<string, unknown>> = {
  [Property in keyof Properties]?: (v: unknown) => boolean;
};

abstract class BaseStore<Properties extends Record<string, unknown>>
  implements Iterable<[keyof Properties, Properties[keyof Properties]]>
{
  protected readonly _lookup: Map<string, unknown> = new Map();
  protected readonly _subscriptions: Map<
    string,
    Set<Partial<Observer<unknown>>>
  > = new Map();

  constructor(
    protected readonly _properties: Extract<keyof Properties, string>[],
    protected readonly _validators?: StoreValidators<Properties>,
    initialValues?: Partial<Properties>,
  ) {
    if (initialValues) {
      for (const [property, value] of Object.entries(initialValues)) {
        if (
          typeof property === 'string' &&
          _properties.includes(property as Extract<keyof Properties, string>)
        ) {
          (this as unknown as Store<Properties>)[property as keyof Properties] =
            value;
        }
      }
    }
  }

  public *[Symbol.iterator](): IterableIterator<
    [keyof Properties, Properties[keyof Properties]]
  > {
    for (const p of this._properties) {
      yield [p, this._lookup.get(p) as Properties[keyof Properties]];
    }
  }

  /*
   * Return keys in the store
   */
  public keys(): IterableIterator<keyof Properties> {
    return this._properties[Symbol.iterator]();
  }

  /*
   * Return values in the store
   */
  public *values(): IterableIterator<Properties[keyof Properties]> {
    for (const p of this._properties) {
      yield this._lookup.get(p) as Properties[keyof Properties];
    }
  }

  /*
   * Return entries in the store
   */
  public *entries(): IterableIterator<
    [keyof Properties, Properties[keyof Properties]]
  > {
    for (const p of this._properties) {
      yield [p, this._lookup.get(p) as Properties[keyof Properties]];
    }
  }

  /*
   * Remove all subscriptions
   */
  public unsubscribeAll(): void {
    for (const observers of this._subscriptions.values()) {
      callCompleteObservers(observers);
    }
    this._subscriptions.clear();
  }
}

/*
 * Factory to create a simple store with observable properties
 * @example
 * ```typescript
 * const myStore = storeFactory<{isOpen: boolean; count: number}>(['isOpen', 'count']);
 * myStore.isOpen
 * myStore.subscribeIsOpen({ next(v: boolean) { console.log(`value is ${v}`); } });
 * ```
 */
export const storeFactory = <Properties extends Record<string, unknown>>(
  properties: Extract<keyof Properties, string>[],
  validators?: StoreValidators<Properties>,
  initialValues?: Partial<Properties>,
): Store<Properties> => {
  class _Store extends BaseStore<Properties> {}

  //define properties and methods on Store
  for (const property of properties) {
    if (typeof property === 'string' && property.length) {
      //Define the property with a getter/setter
      Object.defineProperty(_Store.prototype, property, {
        get(this: _Store) {
          return this._lookup.get(property);
        },
        set(this: _Store, value): void {
          const validator = this._validators && this._validators[property];
          let isValid = true;
          const observers = this._subscriptions.get(property);
          if (typeof validator === 'function' && observers) {
            try {
              isValid = validator(value);
            } catch (err) {
              callErrorObservers(observers, err);
              throw err;
            }
          }
          if (isValid) {
            //Set property and call its observers
            const currentValue = this._lookup.get(property);
            if (currentValue !== value) {
              this._lookup.set(property, value);
              if (observers) {
                callNextObservers(observers, value);
              }
            }
          } else {
            const err = new Error(
              `Can't set ${property} with invalid value "${value}"`,
            );
            if (observers) {
              callErrorObservers(observers, err);
            }
            //Could also throw in the validator
            throw err;
          }
        },
        enumerable: true,
      });

      //Add subscribeProperty method
      const [firstChar, ...remainingChars] = property;
      const subscribePropertyMethodName = `subscribe${firstChar.toUpperCase()}${remainingChars.join(
        '',
      )}`;
      //eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore (we want to create this property)
      _Store.prototype[subscribePropertyMethodName] = function (
        this: _Store,
        observer: Partial<Observer<unknown>>,
      ) {
        let observers: Set<Partial<Observer<unknown>>>;
        observers = this._subscriptions.get(property) as Set<
          Partial<Observer<unknown>>
        >; //it will be defined before it is used
        if (!observers) {
          this._subscriptions.set(property, (observers = new Set())); //set and cache
        }
        observers.add(observer);
        return {
          unsubscribe: () => {
            const currentObservers = this._subscriptions.get(property);
            if (currentObservers) {
              currentObservers.delete(observer);
              if (!currentObservers.size) {
                this._subscriptions.delete(property);
              }
            }
          },
        };
      } as ObservableLike['subscribe']; //Same interface as 'subscribe', but we're assigning to `subscribePropertyMethodName`
    }
  }

  //by inspection, the returned store implements Store<Properties>
  return new _Store(
    properties,
    validators,
    initialValues,
  ) as unknown as Store<Properties>;
};
